import {
  Button,
  Checkbox,
  FormGroup,
  Input,
  Label,
  Row,
  Select,
  Textarea,
} from '@/components'
import { useAxios } from '@/context/axiosContext'
import { useDialog } from '@/context/dialogContext'
import { useRedis } from '@/hooks/useRedis'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Col } from 'styled-bootstrap-grid'

type TInput = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
type CategoryCode = '00' | '01' | '02' | '10' | '20' | '30' // 그린피, 캐디피, 카트피, 로그인, 선행작업, 후행작업

type CategoryInfo = {
  seq?: number // 일련번호-등록시 발급, 테스트에 사용
  mshipYn: string // 회원여부
  scrapType: string // 스크래핑타입
  holeType: string // 홀타입
  reqUrl: string // 요청 URL
  reqMtdTypeCd: string // 요청 메소드 타입 코드
  reqDataSndTypeCd: string // 요청 데이터 전송 타입 코드
  resTypeCd: string // 응답 방식 코드
  loginRequiredYn: string // 로그인 필수 여부
  preProcRequiredYn: string // 선행 프로세스 필수 여부
  rem: string //비고
  headers: Array<{ code: string; remark: string; rem: string }> // 헤더 정보
  data: Array<{ reqKey: string; reqData: string; rem: string }> // 요청 데이터 정보
  selectors: Array<{ dataTypeCd: string; selector: string; rem: string }> // 선택자 정보
}

const ScrpInfoMgmt: React.FC = () => {
  const axios = useAxios()
  const navigate = useNavigate()
  const location = useLocation()
  const { showAlert, showConfirm } = useDialog()
  const { getComCodes, getComCodeValue } = useRedis()

  const reqDivCodes = getComCodes('REQ_DIV_CD') // 요청 구분 코드
  const reqMtdTypeCodes = getComCodes('REQ_MTD_TYPE_CD') // 요청 메소드 타입 코드
  const reqDataSndTypeCodes = getComCodes('REQ_DATA_SND_TYPE_CD') // 요청 데이터 전송 타입 코드
  const resTypeCodes = getComCodes('RES_TYPE_CD') // 응답 방식 코드
  const headerCodes = getComCodes('HEADER_CD') // 헤더 코드
  const dataTypeCd = getComCodes('DATA_TYPE_CD') // 데이터 타입 코드

  const [mainInfo, setMainInfo] = useState({
    cmpnyCd: location.state.cmpnyCd || '',
    bizCd: location.state.bizCd || '',
  })

  const initCategories: Record<CategoryCode, CategoryInfo[]> = {
    // 그린피
    '00': [
      {
        mshipYn: getComCodes('MSHIP_YN')[0]?.code || 'N',
        scrapType: getComCodes('SCRAP_TYPE')[0]?.code || '10',
        holeType: getComCodes('HOLE_TYPE')[0]?.code || '18',
        reqUrl: '',
        reqMtdTypeCd: reqMtdTypeCodes[0]?.code || '',
        reqDataSndTypeCd: reqDataSndTypeCodes[0]?.code || '',
        resTypeCd: resTypeCodes[0]?.code || '',
        loginRequiredYn: 'N',
        preProcRequiredYn: 'N',
        rem: '',
        headers: [],
        data: [],
        selectors: [],
      },
    ],
    // 캐디피
    '01': [
      {
        mshipYn: '',
        scrapType: '',
        holeType: '',
        reqUrl: '',
        reqMtdTypeCd: reqMtdTypeCodes[0]?.code || '',
        reqDataSndTypeCd: reqDataSndTypeCodes[0]?.code || '',
        resTypeCd: resTypeCodes[0]?.code || '',
        loginRequiredYn: 'N',
        preProcRequiredYn: 'N',
        rem: '',
        headers: [],
        data: [],
        selectors: [],
      },
    ],
    // 카트피
    '02': [
      {
        mshipYn: '',
        scrapType: '',
        holeType: '',
        reqUrl: '',
        reqMtdTypeCd: reqMtdTypeCodes[0]?.code || '',
        reqDataSndTypeCd: reqDataSndTypeCodes[0]?.code || '',
        resTypeCd: resTypeCodes[0]?.code || '',
        loginRequiredYn: 'N',
        preProcRequiredYn: 'N',
        rem: '',
        headers: [],
        data: [],
        selectors: [],
      },
    ],
    // 요청정보 - 로그인
    '10': [
      {
        mshipYn: '',
        scrapType: '',
        holeType: '',
        reqUrl: '',
        reqMtdTypeCd: reqMtdTypeCodes[0]?.code || '',
        reqDataSndTypeCd: reqDataSndTypeCodes[0]?.code || '',
        resTypeCd: resTypeCodes[0]?.code || '',
        loginRequiredYn: 'N',
        preProcRequiredYn: 'N',
        rem: '',
        headers: [],
        data: [],
        selectors: [],
      },
    ],
    // 요청정보 - 선행프로세스
    '20': [
      {
        mshipYn: '',
        scrapType: '',
        holeType: '',
        reqUrl: '',
        reqMtdTypeCd: reqMtdTypeCodes[0]?.code || '',
        reqDataSndTypeCd: reqDataSndTypeCodes[0]?.code || '',
        resTypeCd: resTypeCodes[0]?.code || '',
        loginRequiredYn: 'N',
        preProcRequiredYn: 'N',
        rem: '',
        headers: [],
        data: [],
        selectors: [],
      },
    ],
    // 후행작업
    '30': [
      {
        mshipYn: '',
        scrapType: '',
        holeType: '',
        reqUrl: '',
        reqMtdTypeCd: reqMtdTypeCodes[0]?.code || '',
        reqDataSndTypeCd: reqDataSndTypeCodes[0]?.code || '',
        resTypeCd: resTypeCodes[0]?.code || '',
        loginRequiredYn: 'N',
        preProcRequiredYn: 'N',
        rem: '',
        headers: [],
        data: [],
        selectors: [],
      },
    ],
  }

  // 공통코드 - REQ_DIV_CD 요청구분코드
  const [categories, setCategories] =
    useState<Record<CategoryCode, CategoryInfo[]>>(initCategories)

  const initSelectedCategories: Record<CategoryCode, boolean> = {
    '00': false,
    '01': false,
    '02': false,
    '10': false,
    '20': false,
    '30': false,
  }

  const [selectedCategories, setSelectedCategories] = useState<
    Record<CategoryCode, boolean>
  >(initSelectedCategories)

  const handleCategoryChange = (categoryCode: CategoryCode) => {
    setSelectedCategories((prev) => {
      const updated = {
        ...prev,
        [categoryCode]: !prev[categoryCode],
      }

      // 체크박스가 해제되면 해당 카테고리의 입력값 초기화
      if (!updated[categoryCode]) {
        setCategories((prevCategories) => ({
          ...prevCategories,
          [categoryCode]: [
            {
              reqUrl: '',
              reqMtdTypeCd: reqMtdTypeCodes[0]?.code || '',
              reqDataSndTypeCd: reqDataSndTypeCodes[0]?.code || '',
              resTypeCd: resTypeCodes[0]?.code || '',
              loginRequiredYn: 'N',
              preProcRequiredYn: 'N',
              rem: '',
              headers: [],
              data: [],
              selectors: [],
            },
          ],
        }))
      }

      // 그린피, 캐디피, 카트피가 모두 해제되었는지 확인
      const hasMainCategory = ['00', '01', '02'].some(
        (code) => updated[code as CategoryCode],
      )

      if (!hasMainCategory) {
        updated['10'] = false
        updated['20'] = false

        // 로그인과 선행작업 카테고리 초기화
        setCategories((prevCategories) => ({
          ...prevCategories,
          '10': [
            {
              ...prevCategories['10'][0],
              reqUrl: '',
              reqMtdTypeCd: reqMtdTypeCodes[0]?.code || '',
              reqDataSndTypeCd: reqDataSndTypeCodes[0]?.code || '',
              resTypeCd: resTypeCodes[0]?.code || '',
              rem: '',
              headers: [],
              data: [],
              selectors: [],
            },
          ],
          '20': [
            {
              ...prevCategories['20'][0],
              reqUrl: '',
              reqMtdTypeCd: reqMtdTypeCodes[0]?.code || '',
              reqDataSndTypeCd: reqDataSndTypeCodes[0]?.code || '',
              resTypeCd: resTypeCodes[0]?.code || '',
              rem: '',
              headers: [],
              data: [],
              selectors: [],
            },
          ],
        }))
      }

      return updated
    })
  }

  const highlightFieldList = ['mshipYn', 'scrapType', 'holeType']
  const highlightClass = [
    'select-color-0',
    'select-color-1',
    'select-color-2',
    'select-color-3',
  ]

  const handleCategoryInfoChange = (
    categoryCode: CategoryCode,
    fieldName: keyof CategoryInfo,
    value: string,
    categoryIndex: number,
    index?: number,
    subField?: string,
  ) => {
    if (highlightFieldList.some((field) => field === fieldName)) {
      const targetTag: HTMLSelectElement | any = document.getElementById(
        `${categoryCode}-${categoryIndex}-${fieldName}`,
      )
      if (targetTag) {
        const selectedIndex = targetTag.selectedIndex
        highlightClass.forEach((cls) => targetTag.classList.remove(cls))

        if (highlightClass[selectedIndex]) {
          targetTag.classList.add(highlightClass[selectedIndex])
        }
      }
    }

    // 현재 카테고리의 필드 값만 업데이트
    setCategories((prev) => {
      const updatedCategories = { ...prev }

      // 로그인필수여부나 선행프로세스필수여부가 변경될 때 추가 로직
      if (
        fieldName === 'loginRequiredYn' ||
        fieldName === 'preProcRequiredYn'
      ) {
        // 현재 카테고리만 업데이트 (동기화하지 않음)
        updatedCategories[categoryCode][categoryIndex] = {
          ...updatedCategories[categoryCode][categoryIndex],
          [fieldName]: value,
        }

        // 모든 카테고리 중 하나라도 로그인필수여부가 Y인지 확인
        const hasLoginRequired = Object.entries(updatedCategories)
          .filter(([code]) => ['00', '01', '02'].includes(code))
          // .some(([_, info]) => info.loginRequiredYn === 'Y')
          .some(
            ([_, infoArray]) =>
              Array.isArray(infoArray) &&
              infoArray.some((info) => info.loginRequiredYn === 'Y'),
          )

        // 모든 카테고리 중 하나라도 선행프로세스필수여부가 Y인지 확인
        const hasPreProcRequired = Object.entries(updatedCategories)
          .filter(([code]) => ['00', '01', '02'].includes(code))
          // .some(([_, info]) => info.preProcRequiredYn === 'Y')
          .some(
            ([_, infoArray]) =>
              Array.isArray(infoArray) &&
              infoArray.some((info) => info.preProcRequiredYn === 'Y'),
          )

        // 로그인필수여부 처리
        if (fieldName === 'loginRequiredYn') {
          setSelectedCategories((prev) => ({
            ...prev,
            '10': hasLoginRequired,
          }))

          // 로그인필수여부가 N으로 변경되고 모든 카테고리에서 N이면 로그인 카테고리 초기화
          if (value === 'N' && !hasLoginRequired) {
            updatedCategories['10'] = [
              {
                ...updatedCategories['10'][0],
                reqUrl: '',
                reqMtdTypeCd: reqMtdTypeCodes[0]?.code || '',
                reqDataSndTypeCd: reqDataSndTypeCodes[0]?.code || '',
                resTypeCd: resTypeCodes[0]?.code || '',
                rem: '',
                headers: [],
                data: [],
                selectors: [],
              },
            ]
          }
        }

        // 선행프로세스필수여부 처리
        if (fieldName === 'preProcRequiredYn') {
          setSelectedCategories((prev) => ({
            ...prev,
            '20': hasPreProcRequired,
          }))

          // 선행프로세스필수여부가 N으로 변경되고 모든 카테고리에서 N이면 선행작업 카테고리 초기화
          if (value === 'N' && !hasPreProcRequired) {
            updatedCategories['20'] = [
              {
                ...updatedCategories['20'][0],
                reqUrl: '',
                reqMtdTypeCd: reqMtdTypeCodes[0]?.code || '',
                reqDataSndTypeCd: reqDataSndTypeCodes[0]?.code || '',
                resTypeCd: resTypeCodes[0]?.code || '',
                rem: '',
                headers: [],
                data: [],
                selectors: [],
              },
            ]
          }
        }

        return updatedCategories
      }

      updatedCategories[categoryCode as CategoryCode][categoryIndex] = {
        ...updatedCategories[categoryCode as CategoryCode][categoryIndex],
        [fieldName]:
          (fieldName === 'headers' ||
            fieldName === 'selectors' ||
            fieldName === 'data') &&
          index !== undefined &&
          subField
            ? updatedCategories[categoryCode][categoryIndex][fieldName].map(
                (item, i) =>
                  i === index
                    ? {
                        ...item,
                        [subField]: value,
                        ...(fieldName === 'headers' && subField === 'code'
                          ? {
                              remark:
                                headerCodes.find((hc) => hc.code === value)
                                  ?.remark || '',
                            }
                          : {}),
                      }
                    : item,
              )
            : value,
      }

      return updatedCategories
    })
  }

  const addHeader = (categoryCode: CategoryCode, categoryIndex: number) => {
    setCategories((prev) => ({
      ...prev,
      // [categoryCode]: {
      //   ...prev[categoryCode],
      //   headers: [
      //     ...prev[categoryCode][categoryIndex].headers,
      //     {
      //       code: headerCodes[0]?.code || '',
      //       remark: headerCodes[0]?.remark || '',
      //       rem: '',
      //     },
      //   ],
      // },
      [categoryCode]: prev[categoryCode].map((category, idx) =>
        idx == categoryIndex
          ? {
              ...category,
              headers: [
                ...prev[categoryCode][categoryIndex].headers,
                {
                  code: headerCodes[0]?.code || '',
                  remark: headerCodes[0]?.remark || '',
                  rem: '',
                },
              ],
            }
          : category,
      ),
    }))
  }

  const deleteHeader = (
    categoryCode: CategoryCode,
    categoryIndex: number,
    index: number,
  ) => {
    setCategories((prev) => ({
      ...prev,
      // [categoryCode]: {
      //   ...prev[categoryCode],
      //   headers: prev[categoryCode][categoryIndex].headers.filter((_, i) => i !== index),
      // },
      [categoryCode]: prev[categoryCode].map((category, idx) =>
        idx == categoryIndex
          ? {
              ...category,
              headers: [
                ...prev[categoryCode][categoryIndex].headers.filter(
                  (_, i) => i !== index,
                ),
              ],
            }
          : category,
      ),
    }))
  }

  const addData = (categoryCode: CategoryCode, categoryIndex: number) => {
    setCategories((prev) => ({
      ...prev,
      // [categoryCode]: {
      //   ...prev[categoryCode],
      //   data: [
      //     ...prev[categoryCode][categoryIndex].data,
      //     { reqKey: '', reqData: '', rem: '' },
      //   ],
      // },
      [categoryCode]: prev[categoryCode].map((category, idx) =>
        idx == categoryIndex
          ? {
              ...category,
              data: [
                ...prev[categoryCode][categoryIndex].data,
                { reqKey: '', reqData: '', rem: '' },
              ],
            }
          : category,
      ),
    }))
  }

  const deleteData = (
    categoryCode: CategoryCode,
    categoryIndex: number,
    index: number,
  ) => {
    setCategories((prev) => ({
      ...prev,
      // [categoryCode]: {
      //   ...prev[categoryCode],
      //   data: prev[categoryCode].data.filter((_, i) => i !== index),
      // },
      [categoryCode]: prev[categoryCode].map((category, idx) =>
        idx == categoryIndex
          ? {
              ...category,
              data: [
                ...prev[categoryCode][categoryIndex].data.filter(
                  (_, i) => i !== index,
                ),
              ],
            }
          : category,
      ),
    }))
  }

  const addSelector = (categoryCode: CategoryCode, categoryIndex: number) => {
    setCategories((prev) => ({
      ...prev,
      // [categoryCode]: {
      //   ...prev[categoryCode],
      //   selectors: [
      //     ...prev[categoryCode].selectors,
      //     { dataTypeCd: dataTypeCd[0]?.code || '', selector: '', rem: '' },
      //   ],
      // }
      // ,
      [categoryCode]: prev[categoryCode].map((category, idx) =>
        idx == categoryIndex
          ? {
              ...category,
              selectors: [
                ...prev[categoryCode][categoryIndex].selectors,
                {
                  dataTypeCd: dataTypeCd[0]?.code || '',
                  selector: '',
                  rem: '',
                },
              ],
            }
          : category,
      ),
    }))
  }

  const deleteSelector = (
    categoryCode: CategoryCode,
    categoryIndex: number,
    index: number,
  ) => {
    setCategories((prev) => ({
      ...prev,
      // [categoryCode]: {
      //   ...prev[categoryCode],
      //   selectors: prev[categoryCode].selectors.filter((_, i) => i !== index),
      // },
      [categoryCode]: prev[categoryCode].map((category, idx) =>
        idx == categoryIndex
          ? {
              ...category,
              selectors: [
                ...prev[categoryCode][categoryIndex].selectors.filter(
                  (_, i) => i !== index,
                ),
              ],
            }
          : category,
      ),
    }))
  }

  useEffect(() => {
    if (mainInfo.bizCd) {
      getScrpInfo()
    }
  }, [mainInfo.bizCd])

  useEffect(() => {
    // select 박스 하이라이트 부여하기
    categories['00'].forEach((category, categoryIndex) => {
      highlightFieldList.forEach((fieldName) => {
        const targetTag: HTMLSelectElement | any = document.getElementById(
          `00-${categoryIndex}-${fieldName}`,
        )
        if (targetTag) {
          const selectedIndex = targetTag.selectedIndex
          highlightClass.forEach((cls) => targetTag.classList.remove(cls))

          if (highlightClass[selectedIndex]) {
            targetTag.classList.add(highlightClass[selectedIndex])
          }
        }
      })
    })
  }, [categories])

  const setInitState = (bizScrpInfoList: CategoryInfo[]) => {
    // 초기 상태 복사
    const initialCategories = { ...initCategories }
    const initialSelected = { ...initSelectedCategories }

    // 로그인필수여부와 선행프로세스필수여부 값을 저장할 변수
    let loginRequiredYn = 'N'
    let preProcRequiredYn = 'N'

    // 각 스크래핑 정보 처리
    bizScrpInfoList.forEach((info: any) => {
      if (!info || !info.reqDivCd) return

      const categoryCode = info.reqDivCd as CategoryCode

      // 유효한 카테고리 코드인지 확인
      if (Object.keys(initialCategories).includes(categoryCode)) {
        if (!initialSelected[categoryCode]) {
          // 배열초기화
          initialCategories[categoryCode] = []
        }
        // 카테고리 선택 상태 업데이트
        initialSelected[categoryCode] = true

        // 로그인필수여부와 선행프로세스필수여부 값 업데이트
        if (categoryCode !== '10' && categoryCode !== '20') {
          if (info.loginRequiredYn === 'Y') loginRequiredYn = 'Y'
          if (info.preProcRequiredYn === 'Y') preProcRequiredYn = 'Y'
        }

        // 카테고리 정보 업데이트
        initialCategories[categoryCode].push({
          seq: info.seq || '',
          mshipYn:
            getComCodeValue('MSHIP_YN', info.mshipYn)?.code ||
            getComCodes('MSHIP_YN')[0]?.code ||
            '',
          scrapType:
            getComCodeValue('SCRAP_TYPE', info.scrapType)?.code ||
            getComCodes('SCRAP_TYPE')[0]?.code ||
            '',
          holeType:
            getComCodeValue('HOLE_TYPE', info.holeType)?.code ||
            getComCodes('HOLE_TYPE')[0]?.code ||
            '',
          reqUrl: info.reqUrl || '',
          reqMtdTypeCd: info.reqMtdTypeCd || reqMtdTypeCodes[0]?.code || '',
          reqDataSndTypeCd:
            info.reqDataSndTypeCd || reqDataSndTypeCodes[0]?.code || '',
          resTypeCd: info.resTypeCd || resTypeCodes[0]?.code || '',
          loginRequiredYn: info.loginRequiredYn || 'N',
          preProcRequiredYn: info.preProcRequiredYn || 'N',
          rem: info.rem || '',
          headers: Array.isArray(info.lstBizScrpReqHeaderInfoDto)
            ? info.lstBizScrpReqHeaderInfoDto.map((h: any) => ({
                code: h.headerCd || '',
                remark:
                  headerCodes.find((code) => code.code === h.headerCd)
                    ?.remark || '',
                rem: h.rem || '',
              }))
            : [],
          data: Array.isArray(info.lstBizScrpReqDataInfoDto)
            ? info.lstBizScrpReqDataInfoDto.map((d: any) => ({
                reqKey: d.reqKey || '',
                reqData: d.reqData || '',
                rem: d.rem || '',
              }))
            : [],
          selectors: Array.isArray(info.lstBizScrpResSelectorInfoDto)
            ? info.lstBizScrpResSelectorInfoDto.map((s: any) => ({
                dataTypeCd: s.dataTypeCd || '',
                selector: s.selector || '',
                rem: s.rem || '',
              }))
            : [],
        })
      }
    })

    // 모든 카테고리의 로그인필수여부와 선행프로세스필수여부 동기화
    // Object.keys(initialCategories).forEach((code) => {
    //   if (code !== '10' && code !== '20') {
    //     initialCategories[code as CategoryCode].loginRequiredYn =
    //       loginRequiredYn
    //     initialCategories[code as CategoryCode].preProcRequiredYn =
    //       preProcRequiredYn
    //   }
    // })

    // 로그인필수여부가 Y이면 로그인(10) 카테고리 선택
    if (loginRequiredYn === 'Y') {
      initialSelected['10'] = true
    }

    // 선행프로세스필수여부가 Y이면 선행작업(20) 카테고리 선택
    if (preProcRequiredYn === 'Y') {
      initialSelected['20'] = true
    }

    setSelectedCategories(initialSelected)
    setCategories(initialCategories)
  }

  const getScrpInfo = async () => {
    try {
      const response = await axios.get('/scrp/getScrpInfo', {
        params: { bizCd: mainInfo.bizCd },
      })

      // 응답 데이터 구조 확인
      const responseData = response.data.data
      const bizScrpInfoList = responseData.bizScrpInfoList || []

      if (bizScrpInfoList.length === 0) {
        console.log('데이터 없음')
        return
      }

      // 함수로 변경
      setInitState(bizScrpInfoList)
    } catch (error) {
      console.error('Error fetching data:', error)
      showAlert({
        title: '오류',
        message: '데이터 조회 중 오류가 발생했어요.',
      })
    }
  }

  const handleSave = async () => {
    try {
      // 선택된 카테고리 코드 배열 생성
      const selectedCodes = Object.entries(selectedCategories)
        .filter(([_, isSelected]) => isSelected)
        .map(([code]) => code as CategoryCode)

      // 그린피, 캐디피, 카트피 중 하나라도 선택되어 있는지 확인
      const hasMainCategory = ['00', '01', '02'].some(
        (code) => selectedCategories[code as CategoryCode],
      )

      // 메인 카테고리가 선택되지 않았으면 로그인(10)과 선행작업(20)도 제외
      const filteredCodes = hasMainCategory
        ? selectedCodes
        : selectedCodes.filter((code) => code !== '10' && code !== '20')

      // 필수 입력 항목 체크
      for (const code of filteredCodes) {
        const categoryInfo = categories[code]
        const dupChk = new Set<string>()

        for (let i = 0; i < categoryInfo.length; i++) {
          const key = `${categoryInfo[i].mshipYn}|${categoryInfo[i].scrapType}|${categoryInfo[i].holeType}`
          if (dupChk.has(key)) {
            showAlert({
              title: '오류',
              message: `동일한 요청정보 입니다. 입력값을 확인해주세요.`,
              onClose() {
                document.getElementById(`${code}-${i}-mshipYn`)?.focus()
              },
            })
            return
          }
          dupChk.add(key)
          // 요청 URL 체크
          if (!categoryInfo[i].reqUrl.trim()) {
            const elementId = `${code}-reqUrl`
            showAlert({
              title: '오류',
              message: `${getComCodeValue('REQ_DIV_CD', code)?.codeNm} 요청 URL을 입력해주세요.`,
              onClose() {
                document.getElementById(elementId)?.focus()
              },
            })
            return
          }

          // 데이터 항목 체크
          for (const [index, data] of categoryInfo[i].data.entries()) {
            if (!data.reqKey.trim()) {
              const elementId = `${code}-data-${index}-reqKey`
              showAlert({
                title: '오류',
                message: `${getComCodeValue('REQ_DIV_CD', code)?.codeNm} 데이터 항목을 입력해주세요.`,
                onClose() {
                  document.getElementById(elementId)?.focus()
                },
              })
              return
            }

            //REQ_DATA 빈 문자열로 세팅하는 케이스 확인됨
            // if (!data.reqData.trim()) {
            //   const elementId = `${code}-data-${index}-reqData`
            //   showAlert({
            //     title: '오류',
            //     message: `${getComCodeValue('REQ_DIV_CD', code)?.codeNm} 데이터 항목을 입력해주세요.`,
            //     onClose() {
            //       document.getElementById(elementId)?.focus()
            //     },
            //   })
            //   return
            // }
          }
          // 셀렉터 체크
          for (const [index, selector] of categoryInfo[i].selectors.entries()) {
            if (!selector.selector.trim()) {
              const elementId = `${code}-selector-${index}-selector`
              showAlert({
                title: '오류',
                message: `${getComCodeValue('REQ_DIV_CD', code)?.codeNm} 셀렉터 항목 선택자를 입력해주세요.`,
                onClose() {
                  document.getElementById(elementId)?.focus()
                },
              })
              return
            }
          }
        }
      }

      // 요청 정보 배열 생성
      const reqInfos = filteredCodes
        .map((code) => {
          // 타입 캐스팅
          const categoryInfo = categories[code]

          return categoryInfo.map((category, categoryIndex) => {
            // 로그인(10) 코드이고 선행작업(20)이 선택된 경우 선행프로세스필수여부를 'Y'로 설정
            const isPreProcRequired =
              code === '10' && selectedCategories['20']
                ? 'Y'
                : categoryInfo[categoryIndex].preProcRequiredYn
            return {
              reqDivCd: code,
              reqUrl: categoryInfo[categoryIndex].reqUrl,
              reqMtdTypeCd: categoryInfo[categoryIndex].reqMtdTypeCd,
              reqDataSndTypeCd: categoryInfo[categoryIndex].reqDataSndTypeCd,
              resTypeCd: categoryInfo[categoryIndex].resTypeCd,
              loginRequiredYn: categoryInfo[categoryIndex].loginRequiredYn,
              preProcRequiredYn: isPreProcRequired,
              rem: categoryInfo[categoryIndex].rem,
              mshipYn: categoryInfo[categoryIndex].mshipYn,
              scrapType: categoryInfo[categoryIndex].scrapType,
              holeType: categoryInfo[categoryIndex].holeType,
              headers: categoryInfo[categoryIndex].headers.map((h) => ({
                headerCd: h.code,
                rem: h.rem,
              })),
              data: categoryInfo[categoryIndex].data.map((d) => ({
                reqKey: d.reqKey,
                reqData: d.reqData,
                rem: d.rem,
              })),
              selectors: categoryInfo[categoryIndex].selectors.map((s) => ({
                dataTypeCd: s.dataTypeCd,
                selector: s.selector,
                rem: s.rem,
              })),
            }
          })
        })
        .flat()

      const payload = {
        cmpnyCd: mainInfo.cmpnyCd,
        bizCd: mainInfo.bizCd,
        reqInfos,
      }

      await axios.post('/scrp/insertScrpInfo', payload)
      showAlert({
        type: 'success',
        title: '성공',
        message: '저장이 완료되었어요.',
        onClose: () => {
          getScrpInfo()
        },
      })
    } catch (error) {
      showAlert({ title: '오류', message: '저장 중 오류가 발생했어요.' })
      console.error('Error saving data:', error)
    }
  }

  const handleTestScraping = async (categoryCode: string) => {
    try {
      const response = await axios.post('/scrp/testScraping', null, {
        params: {
          categoryCode,
          bizCd: mainInfo.bizCd,
        },
        timeout: 20000,
      })

      if (response.data.common.resultCode === 'SCRPT0205') {
        showAlert({
          type: 'success',
          title:
            '스크래핑 테스트를 성공적으로 수행어요.\n업체관리 > 가격관리 > 가격이력에서 확인해 주세요.',
        })
      } else if (response.data.common.resultCode === 'SCRPT0200') {
        showAlert({
          type: 'success',
          title:
            '스크래핑 테스트 호출을 성공했어요.\n캐디피, 카트피는 추후 확인 가능해요.',
        })
      } else {
        showAlert({
          title:
            response.data.common.resultMsg ||
            '스크래핑 테스트 결과를 확인해주세요.',
        })
      }
    } catch (error) {
      console.error('Error testing scraping:', error)
      showAlert({
        title: '스크래핑 테스트 중 오류가 발생했어요.',
      })
    }
  }

  const handleSingleTestScraping = async (
    categoryCode: string,
    categoryIndex: number,
  ) => {
    const categoryInfo = categories[categoryCode as CategoryCode]
    const seq = categoryInfo[categoryIndex].seq
    if (seq == null || seq < 0) {
      showAlert({
        type: 'warning',
        title: '스크래핑 테스트는 요청정보 저장후 할 수 있어요.',
      })
      return
    }

    try {
      // const response = await axios.post('/scrp/singleTestScraping', null, {
      const response = await axios.post('/scrp/testScraping', null, {
        params: {
          categoryCode,
          bizCd: mainInfo.bizCd,
          seq: seq,
        },
        timeout: 20000,
      })

      if (response.data.common.resultCode === 'SCRPT0205') {
        showAlert({
          type: 'success',
          title:
            '스크래핑 테스트를 성공적으로 수행어요.\n업체관리 > 가격관리 > 가격이력에서 확인해 주세요.',
        })
      } else if (response.data.common.resultCode === 'SCRPT0200') {
        showAlert({
          type: 'success',
          title:
            '스크래핑 테스트 호출을 성공했어요.\n캐디피, 카트피는 추후 확인 가능해요.',
        })
      } else {
        showAlert({
          title:
            response.data.common.resultMsg ||
            '스크래핑 테스트 결과를 확인해주세요.',
        })
      }
    } catch (error) {
      console.error('Error testing scraping:', error)
      showAlert({
        title: '스크래핑 테스트 중 오류가 발생했어요.',
      })
    }
  }

  const handleCopy = (categoryCode: string) => {
    const categoryInfo = categories[categoryCode as CategoryCode]
    const copyCategory = categoryInfo[0]

    setCategories((prevCategories) => ({
      ...prevCategories,
      [categoryCode as CategoryCode]: [
        ...prevCategories[categoryCode as CategoryCode].slice(0, 1),
        copyCategory,
        ...prevCategories[categoryCode as CategoryCode].slice(1),
      ],
    }))
  }
  const handleDelete = (categoryCode: string, categoryIndex: number) => {
    const categoryInfo = categories[categoryCode as CategoryCode]

    setCategories((prevCategories) => ({
      ...prevCategories,
      [categoryCode as CategoryCode]: [
        ...prevCategories[categoryCode as CategoryCode].slice(0, categoryIndex),
        ...prevCategories[categoryCode as CategoryCode].slice(
          categoryIndex + 1,
        ),
      ],
    }))
  }

  return (
    <>
      <style>
        {`
          .select-color-0 { background-color: #87CEEB; }
          .select-color-1 { background-color: #90EE90; }
          .select-color-2 { background-color: #FFB6B9; }
          .select-color-3 { background-color: #FFD700; }
        `}
      </style>
      <div style={{ width: '100%', height: '100%', marginTop: '50px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            marginBottom: '10px',
            alignItems: 'center',
            marginTop: '50px',
          }}
        >
          <p style={{ fontSize: '24px' }}>스크래핑 정보 관리</p>
          <div style={{ display: 'flex', gap: '5px' }}>
            {/*추가* 오류나면 가격관리 주석 처리*/}
            <Button
              variant="outlined"
              color="neverbe"
              size="xs"
              onClick={() =>
                navigate('/bizMgmt/priceMgmt', {
                  state: {
                    bizCd: mainInfo.bizCd,
                    bizSearchState: location.state?.bizSearchState,
                  },
                })
              }
            >
              가격관리
            </Button>
            <Button
              variant="outlined"
              onClick={() =>
                // showConfirm({
                //   title: '목록 화면으로 이동할까요?',
                //   onConfirm() {
                //     navigate('/bizMgmt', {
                //       state: { bizSearchState: location.state?.bizSearchState },
                //     })
                //   },
                // })
                navigate('/bizMgmt', {
                  state: { bizSearchState: location.state?.bizSearchState },
                })
              }
              color="neverbe"
            >
              목록
            </Button>
            <Button
              onClick={() =>
                showConfirm({
                  title: '저장할까요?',
                  onConfirm() {
                    handleSave()
                  },
                })
              }
              color="neverbe"
            >
              저장
            </Button>
          </div>
        </div>
      </div>

      <Row margin={'20px 0 10px 0px'}>
        <span style={{ fontWeight: 'bold' }}>업체 정보</span>
      </Row>
      <Row>
        <Col col>
          <FormGroup mb="20px">
            <Label display="block" mb="5px" htmlFor="cmpnyCd">
              회사
            </Label>
            <div style={{ marginBottom: '5px' }} />
            <Input
              id="cmpnyCd"
              name="cmpnyCd"
              value={mainInfo.cmpnyCd}
              maxLength={3}
              disabled={true}
            />
          </FormGroup>
        </Col>
        <Col col>
          <FormGroup mb="20px">
            <Label display="block" mb="5px" htmlFor="bizCd">
              업체코드
            </Label>
            <div style={{ marginBottom: '5px' }} />
            <Input
              id="bizCd"
              name="bizCd"
              maxLength={20}
              disabled={true}
              value={mainInfo.bizCd}
              onClick={() => {}}
            />
          </FormGroup>
        </Col>
        <Col col>
          <FormGroup mb="20px">
            <Label display="block" mb="5px" htmlFor="bizNm">
              업체명
            </Label>
            <div style={{ marginBottom: '5px' }} />
            <Input
              id="bizNm"
              name="bizNm"
              maxLength={100}
              disabled={true}
              value={location.state?.bizNm}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col col>
          <FormGroup mb="20px">
            <Label display="block" mb="5px" htmlFor="bizNm">
              회원제여부
            </Label>
            <div style={{ marginBottom: '5px' }} />
            <Input
              id="bizNm"
              name="bizNm"
              maxLength={100}
              disabled={true}
              value={
                getComCodeValue('MSHIP_YN', location.state?.mshipYn)?.codeNm ||
                location.state?.mshipYn
              }
            />
          </FormGroup>
        </Col>
        <Col col>
          <FormGroup mb="20px">
            <Label display="block" mb="5px" htmlFor="holeType">
              홀 타입
            </Label>
            <div style={{ marginBottom: '5px' }} />
            <Input
              id="holeType"
              name="bizNm"
              maxLength={100}
              disabled={true}
              value={
                getComCodeValue('HOLE_TYPE', location.state?.holeType)
                  ?.codeNm || ''
              }
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col>
          <FormGroup mb="20px">
            <Label display="block" mb="5px" htmlFor="rem">
              비고
            </Label>
            <div style={{ marginBottom: '5px' }} />
            <Textarea
              id="rem"
              name="rem"
              value={location.state?.rem}
              disabled={true}
              height={'100%'}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row margin={'10px 0 10px 0px'}>
        {reqDivCodes
          .filter((item) => ['00', '01', '02'].includes(item.code)) // 그린피(00), 캐디피(01), 카트피(02)만 필터링
          .map((item) => (
            <Checkbox
              key={item.code}
              id={`category-${item.code}`}
              name={`category-${item.code}`}
              checked={selectedCategories[item.code as CategoryCode]}
              onChange={() => handleCategoryChange(item.code as CategoryCode)}
              label={item.codeNm}
            />
          ))}
      </Row>

      {Object.entries(selectedCategories)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(
          ([categoryCode, isSelected], index) =>
            isSelected && (
              <div
                key={categoryCode + '_' + index}
                style={{
                  borderBottom: '1px solid',
                  borderColor: '#bdbdbd',
                  paddingBottom: '20px',
                }}
              >
                {categories[categoryCode as CategoryCode].map(
                  (category, categoryIndex) => {
                    return (
                      <>
                        {categoryIndex > 0 ? (
                          <>
                            <br />
                            <hr />
                            <hr />
                            <br />
                          </>
                        ) : (
                          <></>
                        )}
                        <Row margin={'20px 0 10px 0px'}>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              width: '100%',
                            }}
                          >
                            <div
                              style={{
                                fontWeight: 'bold',
                                display: 'flex',
                                gap: '0.5rem',
                                lineHeight: '2rem',
                              }}
                            >
                              요청 정보 -{' '}
                              {
                                getComCodeValue('REQ_DIV_CD', categoryCode)
                                  ?.codeNm
                              }
                              {['00' /* , '01', '02' */].includes(
                                categoryCode,
                              ) &&
                                categoryIndex == 0 && (
                                  // 복사 버튼은 그린피에만 붙이기 (01 02 주석 풀면 캐디피 카트피도 생김 )
                                  <Button
                                    color="neverbe"
                                    variant="outlined"
                                    onClick={() => {
                                      handleCopy(categoryCode)
                                    }}
                                    size="xs"
                                  >
                                    복사
                                  </Button>
                                )}
                              {['00' /* , '01', '02' */].includes(
                                categoryCode,
                              ) && (
                                // 삭제 버튼은 그린피에만 붙이기 (01 02 주석 풀면 캐디피 카트피도 생김 )
                                <>
                                  <Button
                                    color="neverbe"
                                    variant="outlined"
                                    onClick={() => {
                                      handleDelete(categoryCode, categoryIndex)
                                    }}
                                    size="xs"
                                  >
                                    삭제
                                  </Button>
                                  <Button
                                    color="neverbe"
                                    onClick={() => {
                                      handleSingleTestScraping(
                                        categoryCode,
                                        categoryIndex,
                                      )
                                    }}
                                    size="xs"
                                  >
                                    단건 테스트
                                  </Button>
                                </>
                              )}
                            </div>
                            {['00', '01', '02'].includes(categoryCode) &&
                              categoryIndex == 0 && (
                                <Button
                                  color="neverbe"
                                  onClick={() => {
                                    handleTestScraping(categoryCode)
                                  }}
                                >
                                  {
                                    getComCodeValue('REQ_DIV_CD', categoryCode)
                                      ?.codeNm
                                  }{' '}
                                  테스트
                                </Button>
                              )}
                          </div>
                        </Row>

                        {categoryCode == '00' && (
                          // 신규 항목 추가 > 캐디피와 카트피는 정해진게 없어서 그린피에서만 적용
                          <>
                            <Row>
                              <Col md={4}>
                                <FormGroup mb="20px">
                                  <Label
                                    htmlFor={`${categoryCode}-${categoryIndex}-mshipYn`}
                                  >
                                    회원여부
                                  </Label>
                                  <Select
                                    id={`${categoryCode}-${categoryIndex}-mshipYn`}
                                    name={`${categoryCode}-${categoryIndex}-mshipYn`}
                                    value={
                                      categories[categoryCode as CategoryCode][
                                        categoryIndex
                                      ].mshipYn
                                    }
                                    onChange={(e) =>
                                      handleCategoryInfoChange(
                                        categoryCode as CategoryCode,
                                        'mshipYn',
                                        e.target.value,
                                        categoryIndex,
                                      )
                                    }
                                  >
                                    {getComCodes('MSHIP_YN').map((item) => (
                                      <option key={item.code} value={item.code}>
                                        {item.codeNm}
                                      </option>
                                    ))}
                                  </Select>
                                </FormGroup>
                              </Col>
                              <Col md={4}>
                                <FormGroup mb="20px">
                                  <Label
                                    htmlFor={`${categoryCode}-${categoryIndex}-scrapType`}
                                  >
                                    스크래핑타입
                                  </Label>
                                  <Select
                                    id={`${categoryCode}-${categoryIndex}-scrapType`}
                                    name={`${categoryCode}-${categoryIndex}-scrapType`}
                                    value={
                                      categories[categoryCode as CategoryCode][
                                        categoryIndex
                                      ].scrapType
                                    }
                                    onChange={(e) =>
                                      handleCategoryInfoChange(
                                        categoryCode as CategoryCode,
                                        'scrapType',
                                        e.target.value,
                                        categoryIndex,
                                      )
                                    }
                                  >
                                    {getComCodes('SCRAP_TYPE').map((item) => (
                                      <option key={item.code} value={item.code}>
                                        {item.codeNm}
                                      </option>
                                    ))}
                                  </Select>
                                </FormGroup>
                              </Col>
                              <Col md={4}>
                                <FormGroup mb="20px">
                                  <Label
                                    htmlFor={`${categoryCode}-${categoryIndex}-holeType`}
                                  >
                                    요금기준 홀
                                  </Label>
                                  <Select
                                    id={`${categoryCode}-${categoryIndex}-holeType`}
                                    name={`${categoryCode}-${categoryIndex}-holeType`}
                                    value={
                                      categories[categoryCode as CategoryCode][
                                        categoryIndex
                                      ].holeType
                                    }
                                    onChange={(e) =>
                                      handleCategoryInfoChange(
                                        categoryCode as CategoryCode,
                                        'holeType',
                                        e.target.value,
                                        categoryIndex,
                                      )
                                    }
                                  >
                                    {getComCodes('HOLE_TYPE').map((item) => (
                                      <option key={item.code} value={item.code}>
                                        {item.codeNm}
                                      </option>
                                    ))}
                                  </Select>
                                </FormGroup>
                              </Col>
                            </Row>
                          </>
                        )}
                        <Row>
                          <Col col>
                            <FormGroup mb="20px">
                              <Label
                                htmlFor={`${categoryCode}-${categoryIndex}-reqUrl`}
                              >
                                URL
                              </Label>
                              <Input
                                id={`${categoryCode}-${categoryIndex}-reqUrl`}
                                name={`${categoryCode}-${categoryIndex}-reqUrl`}
                                maxLength={300}
                                value={
                                  categories[categoryCode as CategoryCode][
                                    categoryIndex
                                  ].reqUrl
                                }
                                onChange={(e) =>
                                  handleCategoryInfoChange(
                                    categoryCode as CategoryCode,
                                    'reqUrl',
                                    e.target.value,
                                    categoryIndex,
                                  )
                                }
                              />
                            </FormGroup>
                          </Col>
                          <Col col>
                            <FormGroup mb="20px">
                              <Label
                                htmlFor={`${categoryCode}-${categoryIndex}-reqMtdTypeCd`}
                              >
                                메소드
                              </Label>
                              <Select
                                id={`${categoryCode}-${categoryIndex}-reqMtdTypeCd`}
                                name={`${categoryCode}-${categoryIndex}-reqMtdTypeCd`}
                                value={
                                  categories[categoryCode as CategoryCode][
                                    categoryIndex
                                  ].reqMtdTypeCd
                                }
                                onChange={(e) =>
                                  handleCategoryInfoChange(
                                    categoryCode as CategoryCode,
                                    'reqMtdTypeCd',
                                    e.target.value,
                                    categoryIndex,
                                  )
                                }
                              >
                                {getComCodes('REQ_MTD_TYPE_CD').map((item) => (
                                  <option key={item.code} value={item.code}>
                                    {item.codeNm}
                                  </option>
                                ))}
                              </Select>
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col col>
                            <FormGroup mb="20px">
                              <Label
                                htmlFor={`${categoryCode}-${categoryIndex}-reqDataSndTypeCd`}
                              >
                                데이터전송방법
                              </Label>
                              <Select
                                id={`${categoryCode}-${categoryIndex}-reqDataSndTypeCd`}
                                name={`${categoryCode}-${categoryIndex}-reqDataSndTypeCd`}
                                value={
                                  categories[categoryCode as CategoryCode][
                                    categoryIndex
                                  ].reqDataSndTypeCd
                                }
                                onChange={(e) =>
                                  handleCategoryInfoChange(
                                    categoryCode as CategoryCode,
                                    'reqDataSndTypeCd',
                                    e.target.value,
                                    categoryIndex,
                                  )
                                }
                              >
                                {getComCodes('REQ_DATA_SND_TYPE_CD').map(
                                  (item) => (
                                    <option key={item.code} value={item.code}>
                                      {item.codeNm}
                                    </option>
                                  ),
                                )}
                              </Select>
                            </FormGroup>
                          </Col>
                          <Col col>
                            <FormGroup mb="20px">
                              <Label
                                htmlFor={`${categoryCode}-${categoryIndex}-resTypeCd`}
                              >
                                응답방식
                              </Label>
                              <Select
                                id={`${categoryCode}-${categoryIndex}-resTypeCd`}
                                name={`${categoryCode}-${categoryIndex}-resTypeCd`}
                                value={
                                  categories[categoryCode as CategoryCode][
                                    categoryIndex
                                  ].resTypeCd
                                }
                                onChange={(e) =>
                                  handleCategoryInfoChange(
                                    categoryCode as CategoryCode,
                                    'resTypeCd',
                                    e.target.value,
                                    categoryIndex,
                                  )
                                }
                              >
                                {getComCodes('RES_TYPE_CD').map((item) => (
                                  <option key={item.code} value={item.code}>
                                    {item.codeNm}
                                  </option>
                                ))}
                              </Select>
                            </FormGroup>
                          </Col>
                        </Row>
                        {categoryCode !== '10' && categoryCode !== '20' && (
                          <>
                            <Row>
                              <Col col>
                                <FormGroup mb="20px">
                                  <Label
                                    htmlFor={`${categoryCode}-${categoryIndex}-loginRequiredYn`}
                                  >
                                    로그인필수여부
                                  </Label>
                                  <Select
                                    id={`${categoryCode}-${categoryIndex}-loginRequiredYn`}
                                    name={`${categoryCode}-${categoryIndex}-loginRequiredYn`}
                                    value={
                                      categories[categoryCode as CategoryCode][
                                        categoryIndex
                                      ].loginRequiredYn || 'N'
                                    }
                                    onChange={(e: React.ChangeEvent<TInput>) =>
                                      handleCategoryInfoChange(
                                        categoryCode as CategoryCode,
                                        'loginRequiredYn',
                                        e.target.value,
                                        categoryIndex,
                                      )
                                    }
                                  >
                                    <option value="Y">예</option>
                                    <option value="N">아니오</option>
                                  </Select>
                                </FormGroup>
                              </Col>
                              <Col col>
                                <FormGroup mb="20px">
                                  <Label
                                    htmlFor={`${categoryCode}-${categoryIndex}-preProcRequiredYn`}
                                  >
                                    선행프로세스필수여부
                                  </Label>
                                  <Select
                                    id={`${categoryCode}-${categoryIndex}-preProcRequiredYn`}
                                    name={`${categoryCode}-${categoryIndex}-preProcRequiredYn`}
                                    value={
                                      categories[categoryCode as CategoryCode][
                                        categoryIndex
                                      ].preProcRequiredYn || 'N'
                                    }
                                    onChange={(e: React.ChangeEvent<TInput>) =>
                                      handleCategoryInfoChange(
                                        categoryCode as CategoryCode,
                                        'preProcRequiredYn',
                                        e.target.value,
                                        categoryIndex,
                                      )
                                    }
                                  >
                                    <option value="Y">예</option>
                                    <option value="N">아니오</option>
                                  </Select>
                                </FormGroup>
                              </Col>
                            </Row>
                            <Row>
                              <Col>
                                <FormGroup mb="20px">
                                  <Label
                                    htmlFor={`${categoryCode}-${categoryIndex}-rem`}
                                  >
                                    비고
                                  </Label>
                                  <Textarea
                                    resize={false}
                                    id={`${categoryCode}-${categoryIndex}-rem`}
                                    name={`${categoryCode}-${categoryIndex}-rem`}
                                    value={
                                      categories[categoryCode as CategoryCode][
                                        categoryIndex
                                      ].rem
                                    }
                                    onChange={(e: React.ChangeEvent<TInput>) =>
                                      handleCategoryInfoChange(
                                        categoryCode as CategoryCode,
                                        'rem',
                                        e.target.value,
                                        categoryIndex,
                                      )
                                    }
                                    placeholder="비고"
                                    title={
                                      categories[categoryCode as CategoryCode][
                                        categoryIndex
                                      ].rem
                                    }
                                  />
                                </FormGroup>
                              </Col>
                            </Row>
                          </>
                        )}

                        <Row margin={'10px 0 10px 0px'}>
                          <div style={{ display: 'flex', gap: '20px' }}>
                            <div style={{ fontWeight: 'bold' }}>헤더</div>
                            <Button
                              height={'30px'}
                              onClick={() =>
                                addHeader(
                                  categoryCode as CategoryCode,
                                  categoryIndex,
                                )
                              }
                              color="neverbe"
                            >
                              추가
                            </Button>
                          </div>
                        </Row>
                        {categories[categoryCode as CategoryCode][
                          categoryIndex
                        ].headers.map((header, index) => (
                          <Row key={index}>
                            <Col col={2}>
                              <FormGroup mb={'20px'}>
                                <Select
                                  id={`${categoryCode}-header-${categoryIndex}-${index}-code`}
                                  name={`${categoryCode}-header-${categoryIndex}-${index}-code`}
                                  value={header.code}
                                  onChange={(e) =>
                                    handleCategoryInfoChange(
                                      categoryCode as CategoryCode,
                                      'headers',
                                      e.target.value,
                                      categoryIndex,
                                      index,
                                      'code',
                                    )
                                  }
                                >
                                  {headerCodes.map((item) => (
                                    <option key={item.code} value={item.code}>
                                      {item.code}
                                    </option>
                                  ))}
                                </Select>
                              </FormGroup>
                            </Col>
                            <Col col={5}>
                              <FormGroup mb={'20px'}>
                                <Input
                                  id={`${categoryCode}-header-${categoryIndex}-${index}-remark`}
                                  name={`${categoryCode}-header-${categoryIndex}-${index}-remark`}
                                  value={header.remark}
                                  disabled={true}
                                />
                              </FormGroup>
                            </Col>
                            <Col col={5}>
                              <FormGroup mb={'20px'}>
                                <div style={{ display: 'flex', gap: '20px' }}>
                                  <Textarea
                                    height={'38px'}
                                    id={`${categoryCode}-header-${categoryIndex}-${index}-rem`}
                                    name={`${categoryCode}-header-${categoryIndex}-${index}-rem`}
                                    value={header.rem}
                                    onChange={(e) =>
                                      handleCategoryInfoChange(
                                        categoryCode as CategoryCode,
                                        'headers',
                                        e.target.value,
                                        categoryIndex,
                                        index,
                                        'rem',
                                      )
                                    }
                                    placeholder="비고"
                                    title={header.rem}
                                  />
                                  <Button
                                    minWidth={'80px'}
                                    color="neverbe"
                                    onClick={() =>
                                      deleteHeader(
                                        categoryCode as CategoryCode,
                                        categoryIndex,
                                        index,
                                      )
                                    }
                                  >
                                    삭제
                                  </Button>
                                </div>
                              </FormGroup>
                            </Col>
                          </Row>
                        ))}

                        <Row margin={'10px 0 10px 0px'}>
                          <div style={{ display: 'flex', gap: '20px' }}>
                            <div style={{ fontWeight: 'bold' }}>데이터</div>
                            <Button
                              height={'30px'}
                              onClick={() =>
                                addData(
                                  categoryCode as CategoryCode,
                                  categoryIndex,
                                )
                              }
                              color="neverbe"
                            >
                              추가
                            </Button>
                          </div>
                        </Row>
                        {categories[categoryCode as CategoryCode][
                          categoryIndex
                        ].data.map((data, index) => (
                          <Row key={index}>
                            <Col col={2}>
                              <FormGroup mb={'20px'}>
                                <Input
                                  id={`${categoryCode}-data-${categoryIndex}-${index}-reqKey`}
                                  name={`${categoryCode}-data-${categoryIndex}-${index}-reqKey`}
                                  value={data.reqKey}
                                  maxLength={100}
                                  onChange={(e) =>
                                    handleCategoryInfoChange(
                                      categoryCode as CategoryCode,
                                      'data',
                                      e.target.value,
                                      categoryIndex,
                                      index,
                                      'reqKey',
                                    )
                                  }
                                  placeholder="reqKey"
                                />
                              </FormGroup>
                            </Col>
                            <Col col={5}>
                              <FormGroup mb={'20px'}>
                                <Input
                                  id={`${categoryCode}-data-${categoryIndex}-${index}-reqData`}
                                  name={`${categoryCode}-data-${categoryIndex}-${index}-reqData`}
                                  value={data.reqData}
                                  maxLength={300}
                                  onChange={(e) =>
                                    handleCategoryInfoChange(
                                      categoryCode as CategoryCode,
                                      'data',
                                      e.target.value,
                                      categoryIndex,
                                      index,
                                      'reqData',
                                    )
                                  }
                                  placeholder="reqData"
                                />
                              </FormGroup>
                            </Col>
                            <Col col={5}>
                              <FormGroup mb={'20px'}>
                                <div style={{ display: 'flex', gap: '20px' }}>
                                  <Textarea
                                    height={'38px'}
                                    id={`${categoryCode}-data-${categoryIndex}-${index}-rem`}
                                    name={`${categoryCode}-data-${categoryIndex}-${index}-rem`}
                                    value={data.rem}
                                    onChange={(e) =>
                                      handleCategoryInfoChange(
                                        categoryCode as CategoryCode,
                                        'data',
                                        e.target.value,
                                        categoryIndex,
                                        index,
                                        'rem',
                                      )
                                    }
                                    placeholder="비고"
                                    title={data.rem}
                                  />
                                  <Button
                                    minWidth={'80px'}
                                    onClick={() =>
                                      deleteData(
                                        categoryCode as CategoryCode,
                                        categoryIndex,
                                        index,
                                      )
                                    }
                                    color="neverbe"
                                  >
                                    삭제
                                  </Button>
                                </div>
                              </FormGroup>
                            </Col>
                          </Row>
                        ))}

                        <Row margin={'10px 0 10px 0px'}>
                          <div style={{ display: 'flex', gap: '20px' }}>
                            <div style={{ fontWeight: 'bold' }}>셀렉터</div>
                            <Button
                              height={'30px'}
                              onClick={() =>
                                addSelector(
                                  categoryCode as CategoryCode,
                                  categoryIndex,
                                )
                              }
                              color="neverbe"
                            >
                              추가
                            </Button>
                          </div>
                        </Row>
                        {categories[categoryCode as CategoryCode][
                          categoryIndex
                        ].selectors.map((selector, index) => (
                          <Row key={index}>
                            <Col col={2}>
                              <FormGroup mb={'20px'}>
                                <Select
                                  id={`${categoryCode}-selector-${categoryIndex}-${index}-dataTypeCd`}
                                  name={`${categoryCode}-selector-${categoryIndex}-${index}-dataTypeCd`}
                                  value={selector.dataTypeCd}
                                  onChange={(e) =>
                                    handleCategoryInfoChange(
                                      categoryCode as CategoryCode,
                                      'selectors',
                                      e.target.value,
                                      categoryIndex,
                                      index,
                                      'dataTypeCd',
                                    )
                                  }
                                >
                                  {dataTypeCd.map((item) => (
                                    <option key={item.code} value={item.code}>
                                      {item.codeNm}
                                    </option>
                                  ))}
                                </Select>
                              </FormGroup>
                            </Col>
                            <Col col={5}>
                              <FormGroup mb={'20px'}>
                                <Input
                                  id={`${categoryCode}-selector-${categoryIndex}-${index}-selector`}
                                  name={`${categoryCode}-selector-${categoryIndex}-${index}-selector`}
                                  value={selector.selector}
                                  maxLength={300}
                                  onChange={(e) =>
                                    handleCategoryInfoChange(
                                      categoryCode as CategoryCode,
                                      'selectors',
                                      e.target.value,
                                      categoryIndex,
                                      index,
                                      'selector',
                                    )
                                  }
                                  placeholder="선택자"
                                />
                              </FormGroup>
                            </Col>
                            <Col col={5}>
                              <FormGroup mb={'20px'}>
                                <div style={{ display: 'flex', gap: '20px' }}>
                                  <Textarea
                                    id={`${categoryCode}-selector-${categoryIndex}-${index}-rem`}
                                    name={`${categoryCode}-selector-${categoryIndex}-${index}-rem`}
                                    height={'38px'}
                                    value={selector.rem}
                                    onChange={(e) =>
                                      handleCategoryInfoChange(
                                        categoryCode as CategoryCode,
                                        'selectors',
                                        e.target.value,
                                        categoryIndex,
                                        index,
                                        'rem',
                                      )
                                    }
                                    placeholder="비고"
                                    title={selector.rem}
                                  />
                                  <Button
                                    minWidth={'80px'}
                                    onClick={() =>
                                      deleteSelector(
                                        categoryCode as CategoryCode,
                                        categoryIndex,
                                        index,
                                      )
                                    }
                                    color="neverbe"
                                  >
                                    삭제
                                  </Button>
                                </div>
                              </FormGroup>
                            </Col>
                          </Row>
                        ))}
                      </>
                    )
                  },
                )}
              </div>
            ),
        )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'right',
          width: '100%',
          marginBottom: '10px',
          alignItems: 'center',
          marginTop: '20px',
        }}
      >
        <div style={{ display: 'flex', gap: '5px' }}>
          <Button
            variant="outlined"
            onClick={() =>
              // showConfirm({
              //   title: '목록 화면으로 이동할까요?',
              //   onConfirm() {
              //     navigate('/bizMgmt', {
              //       state: { bizSearchState: location.state?.bizSearchState },
              //     })
              //   },
              // })
              navigate('/bizMgmt', {
                state: { bizSearchState: location.state?.bizSearchState },
              })
            }
            color="neverbe"
          >
            목록
          </Button>
          <Button
            onClick={() =>
              showConfirm({
                title: '저장할까요?',
                onConfirm() {
                  handleSave()
                },
              })
            }
            color="neverbe"
          >
            저장
          </Button>
        </div>
      </div>
    </>
  )
}

export default ScrpInfoMgmt
